import template from './uc-cross-sell-rules-page.html.twig';
import './uc-cross-sell-rules-page.scss';

const { Component } = Shopware;
const { Criteria } = Shopware.Data;

function emptyRule() {
    return {
        id: null,
        name: '',
        priority: 0,
        active: true,
        trigger_type: 'any',
        trigger_product_ids: [],
        trigger_category_ids: [],
        trigger_tags: [],
        // trigger_type='product_stream' → list of Shopware product-stream UUIDs.
        // OR-logic, same as Tags / Categories: rule fires if the cart matches
        // ANY of these streams. Empty array = unset / no streams configured →
        // trigger never fires.
        trigger_product_stream_ids: [],
        offer_type: 'cross_selling',
        offer_product_ids: [],
        offer_tag_ids: [],
        // offer_type='categories' → merchant-picked categories (mirrors
        // trigger_category_ids on the offer side). The engine then loads
        // top-selling products from these categories via
        // CrossSellRuleEngine::loadCategoriesOffer().
        offer_category_ids: [],
        // Map of { lowercaseProductUuid: percent } — populated only for
        // offer_type='manual'. Empty values mean "use the rule default".
        offer_product_discounts: {},
        // offer_type='product_stream' → list of Shopware product-stream UUIDs.
        // Offer products are drawn from ANY of these streams; results are
        // merged + deduped server-side then capped by `max_offers`.
        offer_product_stream_ids: [],
        discount_percent: 0,
        max_offers: 3,
    };
}

// ── Tooltip HTML sanitizer ──────────────────────────────────────────────────
//
// The field-help tooltips below (v-html="sanitizeTooltip(...)") ship as
// static plugin snippets today, but Shopware's own admin Settings → Snippets
// editor lets ANY user holding the narrower `snippet.editor` ACL privilege
// (not necessarily full system-admin access) override ANY snippet key at
// runtime, including this plugin's — so trusting $tc()'s return value
// directly with v-html would let that role plant a stored-XSS payload (e.g.
// an <img onerror=...>) that fires in every OTHER admin's browser who opens
// this page. The tooltips legitimately use <strong>/<em>/<br> for
// formatting (see the snippet files), so switching to plain text
// interpolation would lose that — this allowlists just those tags instead.
const ALLOWED_TOOLTIP_TAGS = new Set(['STRONG', 'EM', 'B', 'I', 'BR']);

function escapeTooltipText(text) {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function walkTooltipNode(node) {
    let out = '';
    node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            out += escapeTooltipText(child.textContent);
        } else if (child.nodeType === Node.ELEMENT_NODE && ALLOWED_TOOLTIP_TAGS.has(child.tagName)) {
            const tag = child.tagName.toLowerCase();
            out += tag === 'br' ? '<br>' : `<${tag}>${walkTooltipNode(child)}</${tag}>`;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            // Any other element (img, svg, a, script, style, anything
            // carrying an event-handler attribute, ...) — keep its text
            // content only; drop the tag itself and every attribute.
            out += walkTooltipNode(child);
        }
    });
    return out;
}

/**
 * Parses via DOMParser rather than assigning to a live element's innerHTML —
 * deliberate: a DOMParser-parsed document has no browsing context, so per
 * spec it never fetches images or fires scripts/event handlers while being
 * parsed, unlike assigning untrusted HTML straight to a real element's
 * innerHTML, which CAN fire e.g. an <img onerror=...> the instant it's
 * parsed, before any allowlist/strip step below ever runs.
 */
function sanitizeTooltipHtml(html) {
    const doc = new DOMParser().parseFromString(String(html ?? ''), 'text/html');
    return walkTooltipNode(doc.body);
}

function emptySearch() {
    return { query: '', results: [], loading: false, open: false, labels: {} };
}

function emptySearchState() {
    return {
        triggerProducts:   emptySearch(),
        triggerCategories: emptySearch(),
        triggerTags:       emptySearch(),
        triggerStream:     emptySearch(),
        offerProducts:     emptySearch(),
        offerTags:         emptySearch(),
        offerCategories:   emptySearch(),
        offerStream:       emptySearch(),
    };
}

/** Replace a single key in entitySearch with a merged patch — always triggers Vue reactivity */
function patchSearch(current, key, patch) {
    return { ...current, [key]: { ...current[key], ...patch } };
}

Component.register('uc-cross-sell-rules-page', {
    template,

    // 'acl' is injected as a service (per Shopware's own ACL docs), NOT a
    // mixin — Mixin.getByName('acl') doesn't exist and throws
    // "The mixin 'acl' is not registered." Injecting it here exposes it as
    // `this.acl` / `acl` in the template automatically, same as any other
    // injected service.
    inject: ['repositoryFactory', 'ucCrossSellRulesApiService', 'acl'],

    mixins: [
        Shopware.Mixin.getByName('notification'),
    ],

    data() {
        return {
            isLoading: false,
            isSaving: false,
            rules: [],
            showModal: false,
            ruleToDelete: null,
            editingRule: emptyRule(),
            search: emptySearchState(),
            // Per-field validation errors shown inline in the modal.
            // Keys correspond to field names returned by the server's 409 response
            // (e.g. 'priority', 'discount_percent') or set client-side before submit.
            validationErrors: {},
        };
    },

    computed: {
        categoryRepository() {
            return this.repositoryFactory.create('category');
        },

        tagRepository() {
            return this.repositoryFactory.create('tag');
        },

        // Maximum allowed discount percent on a single rule.
        // 100% is permitted (free gift); anything strictly higher is rejected.
        // Keep in sync with CrossSellRulesAdminController::MAX_DISCOUNT_PERCENT.
        maxDiscount() { return 100; },

    },

    watch: {
        /**
         * Three offer types need a cart product to seed from (Cross-selling,
         * Frequently bought together, Top sellers from category) — hidden
         * from the offer-type dropdown for trigger_type = 'empty_cart' (see
         * the twig's v-if on those <option>s), but a merchant could still
         * arrive here already holding one of them selected (e.g. switching
         * an existing "any cart" rule to "empty cart"). Silently swap to
         * 'best_sellers' rather than leaving a now-hidden, meaningless value
         * selected underneath the dropdown.
         */
        'editingRule.trigger_type'(newType) {
            const seedRequiredTypes = ['cross_selling', 'frequently_bought_together', 'category_top_seller'];
            if (newType === 'empty_cart' && seedRequiredTypes.includes(this.editingRule.offer_type)) {
                this.editingRule.offer_type = 'best_sellers';
            }
        },
    },

    created() {
        this._timers = {};         // non-reactive timer store (not in data()) — search debounce, keyed by field
        this._blurTimers = {};     // onBlur close-delay timers, keyed by field
        this._dropdownTimers = {}; // closeDropdown close-delay timers, keyed by field
        this.loadRules();
    },

    /**
     * Clear every pending setTimeout so none of them fire (and touch
     * `this.search`/`this.patchSearch`) after the component has already
     * been torn down. Vue 3 uses beforeUnmount; beforeDestroy is kept
     * alongside it for compat-mode safety (see uc-cross-sell-rules-page's
     * sibling components for the same dual-hook pattern).
     */
    beforeUnmount() {
        this._clearAllTimers();
    },

    beforeDestroy() {
        this._clearAllTimers();
    },

    methods: {

        _clearAllTimers() {
            Object.values(this._timers || {}).forEach(clearTimeout);
            Object.values(this._blurTimers || {}).forEach(clearTimeout);
            Object.values(this._dropdownTimers || {}).forEach(clearTimeout);
        },

        // ─── Helpers ─────────────────────────────────────────────────────

        goToConfig() {
            this.$router.push({ name: 'sellops.ultimate.cart.config' });
        },

        /** See sanitizeTooltipHtml()'s docblock above for why this exists. */
        sanitizeTooltip(snippetKey) {
            return sanitizeTooltipHtml(this.$tc(snippetKey));
        },

        /** Map the picker entityType string to the Shopware repository/entity name */
        _entityName(entityType) {
            return entityType === 'product-stream' ? 'product_stream' : entityType;
        },

        patchSearch(key, patch) {
            this.search = patchSearch(this.search, key, patch);
        },

        // ─── Rules API ───────────────────────────────────────────────────
        //
        // All data access flows through `this.ucCrossSellRulesApiService`,
        // an ApiService subclass registered in main.js. The service uses
        // getBasicHeaders() internally so Authorization + sw-language-id
        // are auto-attached — no per-call header plumbing.

        async loadRules() {
            this.isLoading = true;
            try {
                const data = await this.ucCrossSellRulesApiService.getRules();
                this.rules = Array.isArray(data) ? data : [];
            } catch {
                this.createNotificationError({ message: this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveGeneric') });
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Client-side pre-flight validation. Returns true if the form is OK,
         * false if errors were set (which are surfaced inline in the modal).
         * Server enforces the same rules — this is for UX, not security.
         */
        validateEditingRule() {
            const errors = {};
            const r = this.editingRule;

            // Discount: 0–maxDiscount
            const d = Number(r.discount_percent ?? 0);
            if (Number.isNaN(d) || d < 0) {
                errors.discount_percent = this.$tc('sellops-ultimate-cart.crossSellRules.errors.discountMin');
            } else if (d > this.maxDiscount) {
                errors.discount_percent = this.$tc('sellops-ultimate-cart.crossSellRules.errors.discountMax', 0, { max: this.maxDiscount });
            }

            // Priority: non-negative integer.
            // Duplicates are NOT rejected — the server cascades any existing
            // rules at the chosen priority down by one (same mental model as
            // dragging an item into a list).
            const p = Number(r.priority ?? 0);
            if (!Number.isInteger(p) || p < 0) {
                errors.priority = this.$tc('sellops-ultimate-cart.crossSellRules.errors.priority');
            }

            // Per-product discount overrides: each value must be 0–maxDiscount
            // when present. Empty values are allowed (= use rule default).
            const map = r.offer_product_discounts || {};
            for (const k of Object.keys(map)) {
                const v = map[k];
                if (v === '' || v === null || v === undefined) continue;
                const n = Number(v);
                if (Number.isNaN(n) || n < 0 || n > this.maxDiscount) {
                    errors.offer_product_discounts = this.$tc('sellops-ultimate-cart.crossSellRules.errors.perProduct', 0, { max: this.maxDiscount });
                    break;
                }
            }

            this.validationErrors = errors;
            return Object.keys(errors).length === 0;
        },

        async saveRule() {
            if (!this.validateEditingRule()) return;

            this.isSaving = true;
            const isNew = !this.editingRule.id;
            try {
                if (isNew) {
                    await this.ucCrossSellRulesApiService.createRule(this.editingRule);
                } else {
                    await this.ucCrossSellRulesApiService.updateRule(this.editingRule.id, this.editingRule);
                }
                this.closeModal();
                await this.loadRules();
            } catch (e) {
                // Axios sets `e.response.{status,data}` on non-2xx; 409
                // carries field-level validation details from the server.
                if (e.response?.status === 409) {
                    const body = e.response.data || {};
                    if (body.field) {
                        this.validationErrors = { ...this.validationErrors, [body.field]: body.error || this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveDuplicate') };
                    } else {
                        this.createNotificationError({
                            title:   this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveErrorTitle'),
                            message: body.error || this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveDuplicate'),
                        });
                    }
                } else {
                    this.createNotificationError({
                        title:   this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveErrorTitle'),
                        message: this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveGeneric'),
                    });
                }
            } finally {
                this.isSaving = false;
            }
        },

        deleteRule(rule) {
            this.ruleToDelete = rule;
        },

        cancelDeleteRule() {
            this.ruleToDelete = null;
        },

        async confirmDeleteRule() {
            const rule = this.ruleToDelete;
            this.ruleToDelete = null;
            try {
                await this.ucCrossSellRulesApiService.deleteRule(rule.id);
                // Remove immediately from local array for instant UI feedback,
                // then sync from server to get correct priorities.
                this.rules = this.rules.filter(r => r.id !== rule.id);
                await this.loadRules();
            } catch {
                this.createNotificationError({ message: this.$tc('sellops-ultimate-cart.crossSellRules.errors.saveGeneric') });
            }
        },

        async toggleActive(rule) {
            rule.active = !rule.active;
            try {
                await this.ucCrossSellRulesApiService.updateRule(rule.id, { ...rule });
            } catch {
                // toggle failed — rule state will sync on next load
            }
        },

        async moveUp(index) {
            if (index === 0) return;
            const arr = [...this.rules];
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
            this.rules = arr;
            await this.saveOrder();
        },

        async moveDown(index) {
            if (index === this.rules.length - 1) return;
            const arr = [...this.rules];
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
            this.rules = arr;
            await this.saveOrder();
        },

        async saveOrder() {
            try {
                await this.ucCrossSellRulesApiService.reorderRules(this.rules.map(r => r.id));
                this.rules.forEach((r, i) => { r.priority = i; });
            } catch {
                // reorder failed — priorities will resync on next load
            }
        },

        // ─── Modal ───────────────────────────────────────────────────────

        openCreateModal() {
            this.editingRule = emptyRule();
            this.editingRule.priority = this.rules.length;
            this.search = emptySearchState();
            this.validationErrors = {};
            this.showModal = true;
        },

        openEditModal(rule) {
            this.editingRule = { ...rule };
            this.search = emptySearchState();
            this.validationErrors = {};
            this.showModal = true;

            // Resolve stored IDs → human-readable labels for chips
            const {
                trigger_product_ids,
                trigger_category_ids,
                trigger_tags,
                trigger_product_stream_ids,
                offer_product_ids,
                offer_product_stream_ids,
            } = this.editingRule;
            if (trigger_product_ids?.length)        this.loadLabels('triggerProducts',   'product',        trigger_product_ids);
            if (trigger_category_ids?.length)       this.loadLabels('triggerCategories', 'category',       trigger_category_ids);
            if (trigger_tags?.length)               this.loadLabels('triggerTags',       'tag',            trigger_tags);
            if (trigger_product_stream_ids?.length) this.loadLabels('triggerStream',     'product-stream', trigger_product_stream_ids);
            if (offer_product_ids?.length)          this.loadLabels('offerProducts',     'product',        offer_product_ids);
            if (this.editingRule.offer_tag_ids?.length)
                                                    this.loadLabels('offerTags',         'tag',            this.editingRule.offer_tag_ids);
            if (this.editingRule.offer_category_ids?.length)
                                                    this.loadLabels('offerCategories',   'category',       this.editingRule.offer_category_ids);
            if (offer_product_stream_ids?.length)   this.loadLabels('offerStream',       'product-stream', offer_product_stream_ids);
        },

        closeModal() {
            this.showModal = false;
        },

        // ─── Entity search ───────────────────────────────────────────────

        /** Called on focus AND click — opens the dropdown and loads results.
         *  Must handle both events because after selecting an item the input stays
         *  focused, so a second click doesn't trigger @focus again. */
        onFocus(key, entityType) {
            // If results are already loaded, just reopen
            if (this.search[key].results.length) {
                this.patchSearch(key, { open: true });
                return;
            }
            // Guard against double-call (focus + click fire together on first focus)
            if (this.search[key].loading) return;
            // Set loading=true synchronously so the sibling event sees it and skips
            this.patchSearch(key, { open: true, loading: true });
            this.doSearch(key, entityType);
        },

        /** Called on blur — close after a short delay so mousedown.prevent on results works */
        onBlur(key) {
            if (this._blurTimers[key]) clearTimeout(this._blurTimers[key]);
            this._blurTimers[key] = setTimeout(() => this.patchSearch(key, { open: false }), 220);
        },

        onSearchInput(key, entityType) {
            // Note: the trimmed query itself is recomputed independently in
            // doSearch() below — this handler only needs to flip UI state.
            this.patchSearch(key, { open: true, loading: true });

            if (this._timers[key]) clearTimeout(this._timers[key]);
            this._timers[key] = setTimeout(() => this.doSearch(key, entityType), 280);
        },

        async doSearch(key, entityType) {
            const q          = (this.search[key].query || '').trim();
            const entityName = this._entityName(entityType);

            this.patchSearch(key, { loading: true, open: true });

            const criteria = new Criteria(1, 25);

            if (q) {
                if (entityType === 'product') {
                    // Multi-field OR: name, SKU, or EAN — lets merchants scan/paste
                    criteria.addFilter(Criteria.multi('OR', [
                        Criteria.contains('name',          q),
                        Criteria.contains('productNumber', q),
                        Criteria.contains('ean',           q),
                    ]));
                } else {
                    criteria.addFilter(Criteria.contains('name', q));
                }
            }
            if (entityType === 'product') {
                criteria.addFilter(Criteria.equals('active', true));
            }

            try {
                const result  = await this.repositoryFactory.create(entityName).search(criteria, Shopware.Context.api);
                const results = result.map(item => ({
                    id:            item.id,
                    name:          item.translated?.name || item.name || item.id,
                    productNumber: item.productNumber || '',
                    ean:           item.ean || '',
                }));
                this.patchSearch(key, { loading: false, results });
            } catch {
                this.patchSearch(key, { loading: false, results: [] });
            }
        },

        async loadLabels(key, entityType, ids) {
            if (!ids || !ids.length) return;
            const entityName = this._entityName(entityType);
            const criteria   = new Criteria(1, ids.length + 10);
            criteria.addFilter(Criteria.equalsAny('id', ids));

            try {
                const result = await this.repositoryFactory.create(entityName).search(criteria, Shopware.Context.api);
                const labels = { ...this.search[key].labels };
                result.forEach(item => {
                    labels[item.id] = item.translated?.name || item.name || item.id;
                });
                this.patchSearch(key, { labels });
            } catch {
                // label load failed — chips will show raw IDs as fallback
            }
        },

        selectEntity(key, idsField, id, name) {
            const ids = this.editingRule[idsField] || [];
            if (!ids.includes(id)) {
                this.editingRule[idsField] = [...ids, id];
            }
            const labels = { ...this.search[key].labels, [id]: name };
            // Multi-select UX: keep the dropdown OPEN with its current results
            // so the merchant can rapid-fire several products without re-clicking
            // the search input. The query is cleared so the next keystroke
            // starts a fresh search; the dropdown closes naturally on blur
            // (click outside) via the existing onBlur handler.
            this.patchSearch(key, { query: '', open: true, labels });
        },

        selectSingleEntity(key, field, id, name) {
            this.editingRule[field] = id;
            const labels = { ...this.search[key].labels, [id]: name };
            this.patchSearch(key, { query: '', results: [], open: false, labels });
        },

        removeEntity(idsField, id) {
            this.editingRule[idsField] = (this.editingRule[idsField] || []).filter(i => i !== id);
        },

        // ─── Per-product discount overrides ──────────────────────────────
        //
        // Stored as a map keyed by lowercase, hyphen-stripped product UUID so
        // it round-trips cleanly with `CrossSellRuleEngine::decodeProductDiscounts`
        // and the server's `normaliseProductDiscounts` helper.

        /** Normalise a product id to the map key shape. */
        _productKey(id) {
            return String(id || '').toLowerCase().replace(/-/g, '');
        },

        /** Read the override value for a chip, returning '' if not set so the
         *  input renders empty and the placeholder shows the rule default. */
        getPerProductDiscount(id) {
            const map = this.editingRule.offer_product_discounts || {};
            const key = this._productKey(id);
            const v   = map[key];
            return (v === undefined || v === null || v === '') ? '' : v;
        },

        /** Write the override value for a chip. Empty string deletes the
         *  entry (= "use rule default" for this product). */
        setPerProductDiscount(id, value) {
            const key = this._productKey(id);
            const map = { ...(this.editingRule.offer_product_discounts || {}) };

            if (value === '' || value === null || value === undefined) {
                delete map[key];
            } else {
                const n = Number(value);
                map[key] = Number.isFinite(n) ? n : value;
            }
            this.editingRule.offer_product_discounts = map;
        },

        /** Used when removing a product chip — also drop its discount entry. */
        removePerProductDiscount(id) {
            const key = this._productKey(id);
            const map = { ...(this.editingRule.offer_product_discounts || {}) };
            delete map[key];
            this.editingRule.offer_product_discounts = map;
        },

        /**
         * Placeholder text for the per-product discount input.
         *
         * The global "Discount %" field is hidden whenever offer_type =
         * 'manual', so for manual mode an empty cell simply means "0% — no
         * discount on this row". For any other offer type, the placeholder
         * hints at the rule's default.
         */
        getPerProductDiscountPlaceholder() {
            if (this.editingRule.offer_type === 'manual') {
                return '0';
            }
            const d = Number(this.editingRule.discount_percent || 0);
            return d > 0 ? `${d}` : '0';
        },

        clearSingleEntity(field, key) {
            this.editingRule[field] = null;
            this.patchSearch(key, { labels: {}, query: '', results: [], open: false });
        },

        openDropdown(key) {
            this.patchSearch(key, { open: true });
        },

        closeDropdown(key) {
            if (this._dropdownTimers[key]) clearTimeout(this._dropdownTimers[key]);
            this._dropdownTimers[key] = setTimeout(() => this.patchSearch(key, { open: false }), 200);
        },

        getLabel(key, id) {
            return this.search[key]?.labels?.[id] || id;
        },

        // ─── Display labels ───────────────────────────────────────────────

        triggerLabel(rule) {
            // NOTE: Vue I18n's $tc(key, choice, values) auto-binds `choice`
            // to the {n} placeholder inside the string — that automatic
            // binding wins over anything passed in `values.n`. So `choice`
            // must be the REAL count, not a placeholder 0, or {n} always
            // renders as whatever `choice` was hardcoded to.
            const cnt = (field) => (rule[field] || []).length;
            switch (rule.trigger_type) {
                case 'any':            return this.$tc('sellops-ultimate-cart.crossSellRules.chipTriggerAny');
                case 'products':       { const n = cnt('trigger_product_ids');        return this.$tc('sellops-ultimate-cart.crossSellRules.chipTriggerProducts',   n, { n }); }
                case 'categories':     { const n = cnt('trigger_category_ids');       return this.$tc('sellops-ultimate-cart.crossSellRules.chipTriggerCategories', n, { n }); }
                case 'tags':           { const n = cnt('trigger_tags');               return this.$tc('sellops-ultimate-cart.crossSellRules.chipTriggerTags',       n, { n }); }
                case 'product_stream': { const n = cnt('trigger_product_stream_ids'); return this.$tc('sellops-ultimate-cart.crossSellRules.chipTriggerStreams',    n, { n }); }
                case 'empty_cart':     return this.$tc('sellops-ultimate-cart.crossSellRules.chipTriggerEmptyCart');
                default:               return rule.trigger_type;
            }
        },

        offerLabel(rule) {
            // See triggerLabel() note on Vue I18n's $tc choice/{n} binding.
            const cnt = (field) => (rule[field] || []).length;
            switch (rule.offer_type) {
                case 'cross_selling':              return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferCrossSelling');
                case 'frequently_bought_together': return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferFbt');
                case 'category_top_seller':        return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferCategoryTop');
                case 'manual':                     { const n = cnt('offer_product_ids');        return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferManual',     n, { n }); }
                case 'tags':                       { const n = cnt('offer_tag_ids');             return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferTags',       n, { n }); }
                case 'categories':                 { const n = cnt('offer_category_ids');        return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferCategories', n, { n }); }
                case 'product_stream':             { const n = cnt('offer_product_stream_ids');  return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferStreams',    n, { n }); }
                case 'best_sellers':                return this.$tc('sellops-ultimate-cart.crossSellRules.chipOfferBestSellers');
                default:                           return rule.offer_type;
            }
        },
    },
});
